import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DashboardContainer from "../sideBar/DashboardContainer";
import { useNavigate } from "react-router-dom";
import Loading from "../../helper/Loading";
import Message from "../../helper/messages/Message";
import PageLayout, {Card, PREDICT_STEPS} from "../layout/PageLayout";
import c from "./validationResult.module.css";

const ValidationResult = () => {
  let navigate = useNavigate();
  const chooseTestValidateResult = useSelector(
    (state) => state?.chooseTestValidateReducer
  );
  const { result, status, message, status_code } =
    chooseTestValidateResult?.validateResult;
  const { loading } = chooseTestValidateResult;
  const [isUploaded, setIsUploaded] = useState(false);
  // The AI-comment choice is made on the Compounds step and carried here in
  // sessionStorage; this page only has to honour it. Read at submit rather than
  // into state, so it reflects the answer at the moment the request is built.
  //
  // Trial accounts cannot generate comments. `is_trial` is true only when the
  // account holds NO active PAID subscription - a paying customer can still hold
  // free per-endpoint trials alongside their bundle, so the flag is computed
  // from the paid side in the backend rather than from the presence of a free one.
  const assays = useSelector(
    (state) => state?.availableAssaysReducer?.availableAssaysResult
  );
  const isTrial = assays?.is_trial === true;
  const aiAllowed = assays?.ai_comment_allowed === true;
  const storedPlatformId = localStorage.getItem('platform_id');
  const REQUIRED_PLATFORM_ID = 'b5c052ca-a48b-4f90-94dd-4feba49353d3';

  const handleUploadReportsChange = () => {
    setIsUploaded(!isUploaded);
  };

  const handleClick = () => {
    let smilesArr = [];
    result.forEach((i, k) => {
      smilesArr.push({
        assay_name: i.assay_name,
        valid_smiles: i["valid_smiles"],
        invalid_smiles: i["invalid_smiles"],
      });
    });
    const requestData = {
      is_upload: isUploaded,
      // Checked again here, not only where the box was ticked. The Compounds step
      // asks before the assay list has arrived on a slow connection, so the flags it
      // saw may have been undefined; this is the last point before the request is
      // sent. The portal checks both a third time, server-side, because nothing in a
      // browser is a gate.
      ai_comment: sessionStorage.getItem('ai_comment') === '1' && aiAllowed && !isTrial,
      smiles: smilesArr,
      platform_id: "b5c052ca-a48b-4f90-94dd-4feba49353d3",
    };
    navigate("/predict", { state: requestData });
  };

  const isAllValidSmilesEmpty = result?.every(
    (item) => item.valid_smiles.length === 0
  );

  // Everything that was excluded, in one place. Categories at zero are dropped
  // rather than listed: six counters of which five read "0" buried the one
  // number that actually matters.
  const exclusions = (i) => [
    ["Invalid", i["num_invalid"]],
    ["Duplicates", i["num_duplicate"]],
    ["Salts & mixtures", i["num_salt_mixture"]],
    ["Too long", i["num_too_long"]],
    ["Inorganic", i["num_inorganic"]],
  ].filter(([, n]) => n > 0);

  const excludedTotal = (i) =>
    exclusions(i).reduce((sum, [, n]) => sum + n, 0);

  // Validation runs per endpoint, but the same compound list is checked against
  // each, so the counts are usually identical everywhere. Only the endpoints
  // whose limits differ (max SMILES length in particular) come out different.
  // Detecting that lets 32 endpoints collapse to one line instead of 32 rows.
  const signature = (i) =>
    [i["num_valid"], i["num_invalid"], i["num_duplicate"],
     i["num_salt_mixture"], i["num_too_long"], i["num_inorganic"]].join("|");

  // True for ONE endpoint as well, which it was not before: `uniform` required
  // result.length > 1, so with a single endpoint selected - the commonest case when
  // somebody is trying the product - the breakdown was computed and never rendered.
  // The page said "EXCLUDED 1" and gave no reason at all.
  const sameEverywhere =
    result?.length === 1 ||
    (result?.length > 1 && result.every((i) => signature(i) === signature(result[0])));

  // The note belongs to the several-endpoint case only; with one endpoint there is
  // nothing for it to be identical to.
  const uniform = result?.length > 1 && sameEverywhere;

  // The excluded lines themselves, with the reason the API gave for each. The counts
  // say how many; this says which, and that is what a customer needs to fix their
  // input. Capped: a 500-line paste with 500 bad lines must not become 500 rows.
  const INVALID_SHOWN = 8;
  const invalidRows = (i) => (i?.["invalid_smiles"] || []).map((row) => ({
    // The API sends (position, input, reason, input).
    line: Array.isArray(row) ? row[0] : null,
    input: Array.isArray(row) ? row[1] : String(row),
    reason: Array.isArray(row) ? row[2] : "",
  }));

  // Was a useRef accumulated during render, so every re-render added the prices
  // again and the figure grew - doubled immediately under StrictMode. Derived.
  const totalPrice = useMemo(
    () => (result || []).reduce((sum, i) => sum + (i["total_price"] || 0), 0),
    [result]
  );

  return (
    <DashboardContainer>
      <PageLayout
        title="Validation Result"
        subtitle="Compounds that passed validation are the ones that will be predicted."
        steps={PREDICT_STEPS}
        currentStep={2}
        narrow
      >
        {!loading && status === "success" && result?.length > 0 && (
          <>
            <Card>
              <p className={c.validCount}>
                <span className={c.validNumber}>{result[0]["num_valid"]}</span>
                {" "}valid compound{result[0]["num_valid"] === 1 ? "" : "s"}
                {result.length > 1 && (
                  <span className={c.summaryScope}>
                    {" "}across {result.length} endpoints
                  </span>
                )}
              </p>
              {sameEverywhere && exclusions(result[0]).length > 0 && (
                <ul className={c.exclusionList}>
                  {exclusions(result[0]).map(([label, n]) => (
                    <li key={label}>
                      <span className={c.exclusionCount}>{n}</span> {label}
                    </li>
                  ))}
                </ul>
              )}
              {sameEverywhere && invalidRows(result[0]).length > 0 && (
                <div className={c.excludedDetail}>
                  <p className={c.excludedTitle}>Excluded lines</p>
                  <ul className={c.excludedLines}>
                    {invalidRows(result[0]).slice(0, INVALID_SHOWN).map((row, k) => (
                      <li key={k}>
                        <span className={c.excludedReason}>
                          {row.reason || "Excluded"}
                        </span>
                        <code className={c.excludedInput} title={row.input}>
                          {row.line ? `${row.line}. ` : ""}{row.input}
                        </code>
                      </li>
                    ))}
                  </ul>
                  {invalidRows(result[0]).length > INVALID_SHOWN && (
                    <p className={c.excludedMore}>
                      and {invalidRows(result[0]).length - INVALID_SHOWN} more
                    </p>
                  )}
                  <p className={c.excludedHint}>
                    A structure may be given as a SMILES, or as an InChI starting
                    with <code>InChI=</code>.
                  </p>
                </div>
              )}
              {uniform && (
                <p className={c.uniformNote}>
                  Every selected endpoint validated identically.
                </p>
              )}
            </Card>

            {/* One row per endpoint rather than one card. With every endpoint
                selected the card layout ran to thousands of pixels of near
                identical content. Skipped entirely when the results are uniform,
                since the summary above already says everything. */}
            {!sameEverywhere && (
              <Card padded={false}>
                <div className={c.tableWrap}>
                  <table className={c.resultTable}>
                    <thead>
                      <tr>
                        <th>Endpoint</th>
                        <th className={c.numeric}>Valid</th>
                        <th className={c.numeric}>Excluded</th>
                        {totalPrice > 0 && <th className={c.numeric}>Price</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {result.map((i, k) => (
                        <tr key={k}>
                          <td>{i["assay_title"]}</td>
                          <td className={c.numeric}>{i["num_valid"]}</td>
                          <td className={`${c.numeric} ${c.muted}`}>
                            {excludedTotal(i) || "—"}
                          </td>
                          {totalPrice > 0 && (
                            <td className={c.numeric}>
                              {i["total_price"] ? `${i["total_price"]} USD` : "—"}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {storedPlatformId === REQUIRED_PLATFORM_ID && (
              <div className={c.uploadReports}>
                <input
                  id="uploadReports"
                  type="checkbox"
                  checked={isUploaded}
                  onChange={handleUploadReportsChange}
                />
                <label htmlFor="uploadReports">
                  Upload reports to eLabNext File Storage
                </label>
              </div>
            )}

            <div className={c.validationResultButton}>
              {/* With nothing valid, Submit is disabled and this page was a dead
                  end: the only way out was the sidebar, which loses what was
                  typed. navigate(-1) returns to the endpoint step with the
                  compound list intact, because react-router keeps the state on the
                  history entry. */}
              <button
                type="button"
                className={c.secondary}
                onClick={() => navigate(-1)}
              >
                Back
              </button>
              <div className={c.totalPrice}>
                {totalPrice > 0 && <span>Total price: {totalPrice} USD</span>}
              </div>
              <button disabled={isAllValidSmilesEmpty} onClick={handleClick}>
                Submit
              </button>
            </div>
          </>
        )}
        {status === "error" && status_code !== 200 && !loading && (
          <Message message={message} status={status} status_code={status_code} />
        )}
        {loading && <Loading text={"Validating..."} />}
      </PageLayout>
    </DashboardContainer>
  );
};

export default ValidationResult;
